import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Search, RotateCcw, ChevronRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useLicense } from "@/context/LicenseContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const SoldArchive = () => {
  const navigate = useNavigate();
  const { isReadOnly } = useLicense();
  const [tires, setTires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchSoldTires = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      
      const response = await api.get(`/tires/sold?${params.toString()}`);
      setTires(response.data);
    } catch (error) {
      console.error("Errore nel caricamento archivio venduti:", error);
      console.error("Response:", error.response?.data);
      console.error("Status:", error.response?.status);
      // Non mostrare toast se è semplicemente lista vuota o 404
      if (error.response?.status !== 404) {
        toast.error("Errore nel caricamento dell'archivio");
      }
      setTires([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSoldTires();
  }, [search]);

  const handleRestore = async (code) => {
    if (isReadOnly) {
      toast.error("Licenza scaduta. Rinnova per ripristinare pneumatici.");
      return;
    }

    try {
      await api.post(`/tires/${code}/restore`);
      toast.success("Pneumatico ripristinato in magazzino");
      fetchSoldTires();
    } catch (error) {
      console.error("Errore nel ripristino:", error);
      console.error("Response:", error.response?.data);
      const message = error.response?.data?.detail || "Errore nel ripristino";
      toast.error(message);
      // toast.error("Errore nel ripristino");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  return (
    <div data-testid="sold-archive" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate("/")}
          className="h-12 w-12 p-0"
        >
          <ArrowLeft size={24} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Archivio Venduti</h1>
          <p className="text-muted-foreground text-sm">
            {tires.length} pneumatici venduti
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          size={18}
        />
        <Input
          data-testid="search-input"
          placeholder="Cerca per codice, misura o marca..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-12 bg-input/50 border-input font-mono text-sm"
        />
      </div>

      {/* Sold List */}
      {loading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-card border-border animate-pulse">
              <CardContent className="p-4 h-24" />
            </Card>
          ))}
        </div>
      ) : tires.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar size={28} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Nessun pneumatico venduto</h3>
            <p className="text-muted-foreground">
              L'archivio venduti è vuoto
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {tires.map((tire, index) => (
            <Card
              key={tire.id}
              data-testid={`sold-card-${tire.code}`}
              className="bg-card border-border card-animate"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 cursor-pointer" onClick={() => navigate(`/tyre/${tire.code}`)}>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono font-semibold text-blue-400 text-lg tracking-wider">
                        {tire.code}
                      </span>
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                        Venduto
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-sm">
                      <div>
                        <span className="text-muted-foreground">Misura: </span>
                        <span className="font-medium">{tire.misura}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Marca: </span>
                        <span className="font-medium">{tire.marca}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Qty: </span>
                        <span className="font-medium">{tire.numero_pneumatici}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Venduto: </span>
                        <span className="font-medium">{formatDate(tire.sold_date)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          data-testid={`restore-btn-${tire.code}`}
                          variant="secondary"
                          size="sm"
                          className="h-10"
                          disabled={isReadOnly}
                        >
                          <RotateCcw size={16} className="mr-2" />
                          Ripristina
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Ripristina pneumatico</AlertDialogTitle>
                          <AlertDialogDescription>
                            Vuoi riportare questo pneumatico in magazzino?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleRestore(tire.code)}
                            className="bg-primary text-primary-foreground"
                          >
                            Ripristina
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <ChevronRight 
                      className="text-muted-foreground cursor-pointer" 
                      size={20}
                      onClick={() => navigate(`/tyre/${tire.code}`)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SoldArchive;
